import type { NoticePublishStatus } from '../models/notice-publish.model';

export type { NoticePublishStatus } from '../models/notice-publish.model';

/** `publish_at` の意味: null=下書き、未来=予約、`<= now`=公開済み。 */
export function noticePublishStatus(
  publishAt: string | null | undefined,
  nowMs: number = Date.now(),
): NoticePublishStatus {
  if (publishAt == null || publishAt === '') {
    return 'draft';
  }
  const at = new Date(publishAt).getTime();
  if (Number.isNaN(at)) {
    return 'draft';
  }
  return at <= nowMs ? 'published' : 'scheduled';
}

export function isNoticeLive(
  publishAt: string | null | undefined,
  nowMs: number = Date.now(),
): boolean {
  return noticePublishStatus(publishAt, nowMs) === 'published';
}

/**
 * `ion-datetime` はタイムゾーン変換をしない（壁時計の文字列として扱う）。
 * DB の UTC ISO をピッカー表示用のローカル壁時計（オフセットなし）へ。
 * 例: `2026-09-27T11:26:00.000Z`（JST）→ `2026-09-27T20:26`
 */
export function utcIsoToIonDatetimeLocalValue(utcIso: string): string {
  const d = new Date(utcIso);
  if (Number.isNaN(d.getTime())) {
    return '';
  }
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const h = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${y}-${m}-${day}T${h}:${min}`;
}

/**
 * `ion-datetime` の値（ローカル壁時計、またはオフセット付き ISO）→ DB 用 UTC ISO。
 * オフセットなしは端末ローカルとして解釈する（`Date` の仕様）。
 */
export function ionDatetimeLocalValueToUtcIso(
  localValue: string,
): string | null {
  const trimmed = localValue.trim();
  if (!trimmed) return null;
  const d = new Date(trimmed);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

/** 予約の最短瞬間: 現在より1分超（UTC ISO）。 */
export function minSchedulePublishAtIso(nowMs: number = Date.now()): string {
  return new Date(nowMs + 60_000 + 1).toISOString();
}

/**
 * `ion-datetime` の `[min]` 用（ローカル壁時計・分単位）。
 * 秒付きの最短瞬間を分に切り捨てると「選べるが適用できない」分が残るため、
 * その分の :00 が「1分超」を満たす最初の分へ切り上げる。
 */
export function minSchedulePublishAtIonValue(
  nowMs: number = Date.now(),
): string {
  const earliest = new Date(nowMs + 60_000 + 1);
  if (earliest.getSeconds() > 0 || earliest.getMilliseconds() > 0) {
    earliest.setSeconds(0, 0);
    earliest.setMinutes(earliest.getMinutes() + 1);
  }
  return utcIsoToIonDatetimeLocalValue(earliest.toISOString());
}

/**
 * ピッカーの value が min 未満／未設定だと、操作時に min へクランプされて表示が跳ねる。
 * 予約 UI では最初から min 以上の値を持たせる。
 */
export function clampIonDatetimeToMin(
  ionValue: string | null | undefined,
  minIon: string,
): string {
  if (ionValue == null || ionValue.trim() === '') {
    return minIon;
  }
  const at = new Date(ionValue).getTime();
  const min = new Date(minIon).getTime();
  if (Number.isNaN(at) || Number.isNaN(min) || at < min) {
    return minIon;
  }
  return ionValue;
}

export function isValidSchedulePublishAt(
  publishAtIso: string,
  nowMs: number = Date.now(),
): boolean {
  const at = new Date(publishAtIso).getTime();
  if (Number.isNaN(at)) return false;
  return at > nowMs + 60_000;
}
